import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";
import { ensureMounted } from "@/components/aht/Deferred";

export const EASE = [0.22, 1, 0.36, 1];
export const SPRING = { type: "spring", stiffness: 300, damping: 30 };

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

export function useMediaQuery(query) {
  const get = () => (typeof window !== "undefined" ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

// Count from 0 to target once `start` is true. Reduced motion shows the final value.
export function useCountUp(target, start, { duration = 0.9, delay = 0 } = {}) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? target : 0);
  const done = useRef(false);
  useEffect(() => {
    if (reduce) {
      setValue(target);
      return undefined;
    }
    if (!start || done.current) return undefined;
    const controls = animate(0, target, {
      duration,
      delay,
      ease: EASE,
      onUpdate: (v) => setValue(v),
      onComplete: () => {
        done.current = true;
        setValue(target);
      },
    });
    return () => controls.stop();
  }, [start, target, reduce, duration, delay]);
  return value;
}

// Smooth scroll that honors reduced motion and the sticky nav height.
export function scrollToEl(el, reduce) {
  if (!el) return;
  const nav = 52;
  const top = el.getBoundingClientRect().top + window.scrollY - nav;
  window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
}

// Mounts any deferred sections above (and including) the target first, so it does not move mid-scroll.
export function scrollToId(id, reduce) {
  const el = document.getElementById(id);
  if (!el) return;
  if (document.querySelector("[data-placeholder]")) {
    ensureMounted(id).then(() => scrollToEl(document.getElementById(id), reduce));
    return;
  }
  scrollToEl(el, reduce);
}

// Which section is under the reading line (35% down the viewport).
export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const line = window.innerHeight * 0.35;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ids]);
  return active;
}

const IGNORE_ROLES = ["slider", "radio", "tab", "spinbutton", "textbox", "combobox", "listbox", "menu"];

// Arrow up/down and J/K jump between [data-stop] elements, for presenting on a TV.
export function useKeyboardStops(enabled = true) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      const next = key === "ArrowDown" || key === "j" || key === "J";
      const prev = key === "ArrowUp" || key === "k" || key === "K";
      if (!next && !prev) return;
      const t = e.target;
      if (t && t.closest) {
        const tag = (t.tagName || "").toLowerCase();
        if (["input", "textarea", "select"].includes(tag) || t.isContentEditable) return;
        const role = t.getAttribute && t.getAttribute("role");
        if (role && IGNORE_ROLES.includes(role)) return;
        if (t.closest("[data-keynav-ignore]") || t.closest("[role='dialog']")) return;
      }
      if (document.documentElement.hasAttribute("data-overlay-open")) return;
      const stops = Array.from(document.querySelectorAll("[data-stop]"))
        .map((el) => ({ el, top: el.getBoundingClientRect().top + window.scrollY - 52 }))
        .sort((a, b) => a.top - b.top);
      if (!stops.length) return;
      const y = window.scrollY;
      const tol = 6;
      let target;
      if (next) target = stops.find((s) => s.top > y + tol);
      else target = [...stops].reverse().find((s) => s.top < y - tol);
      if (!target) return;
      e.preventDefault();
      window.scrollTo({ top: Math.max(0, target.top), behavior: reduce ? "auto" : "smooth" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, reduce]);
}

export function useImageAvailable(src) {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    if (!src) {
      setOk(false);
      return undefined;
    }
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setOk(img.naturalWidth > 0);
    img.onerror = () => alive && setOk(false);
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);
  return ok;
}
